import { capitalise } from 'lib/string/stringUtils'
import { createSelector } from 'reselect'
import { knownPriceBreakdownTitle } from 'checkout/constants/priceBreakdownTitles'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import getInsuranceItemsView from '../itemViews/getInsuranceItemsView'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { checkoutContainsFlightsWithEmbeddedInsurance, getFlightItems } from '../flights'
import { checkoutContainsBedbankWithCFMRFlexPackage, isCheckoutPostPurchaseInsurance } from '../insurance'
import { isStandaloneFlight } from 'checkout/selectors/flightSearchSelectors'
import { getCheckoutRouteFromURL } from 'checkout/lib/utils/misc/steps'
import { isCheckoutReviewAndPaymentStepId } from 'checkout/constants/pages'
import { getLuxVerticals } from 'lib/insurance/insuranceUtils'
import { isFlightWithEmbeddedInsurance } from 'checkout/utils'
import { formatInlineList } from 'lib/format/formatList'
import type { QuoteVerticals } from 'api/insurance'
import { INSURANCE_TYPE_PASSPORT_CARD, PRODUCT_ID_PASSPORT_CARD } from 'constants/insurance'
import { dateFormattingLocaleFromGeo, intlDateFormatter } from 'lib/datetime/dateUtils'

function formatPolicyName(policyName: string, isPostPurchase: boolean) {
  return isPostPurchase ? policyName.replace(/Comprehensive\s*/g, '') : policyName
}

function getInsuranceBreakdownItem(
  item: App.Checkout.InsuranceItemView,
  isPostPurchase: boolean,
  dateFormattingLocale: string,
): App.Checkout.InsuranceItemBreakdownView {
  const intl = getCurrentIntl()
  const additionalInfo: Array<App.Checkout.ItemBreakdownViewAdditionalText> = [
    ...item.policyNames.map<App.Checkout.ItemBreakdownViewAdditionalText>(policyName => ({ text: formatPolicyName(policyName, isPostPurchase) })),
    {
      text: intl.formatMessage(
        {
          id: '97DYfuhdEQZmJFW8EU3K',
          defaultMessage: '{startDate} - {endDate}',
          description: 'Insurance coverage date range in checkout breakdown',
        },
        {
          startDate: intlDateFormatter(item.startDate, INTL_DMY_CASUAL_FORMAT, dateFormattingLocale),
          endDate: intlDateFormatter(item.endDate, INTL_DMY_CASUAL_FORMAT, dateFormattingLocale),
        },
      ),
    },
  ]
  // Destination countries can legitimately be absent from the source data — post-purchase
  // items read them off the original order item and quote-derived lists can be empty — so
  // skip the line instead of rendering a bare "Covered in:" label.
  if (item.destinationCountries.length > 0) {
    additionalInfo.push({
      text: intl.formatMessage(
        {
          id: 'uZwnotT7ONlxvqbx-AsO',
          defaultMessage: 'Covered in: {countries}',
          description: 'Insurance destination countries in checkout breakdown',
        },
        { countries: item.destinationCountries.join(', ') },
      ),
    })
  }
  return {
    itemId: item.item.itemId,
    transactionKey: item.item.transactionKey,
    title: item.policyNames.length ? '' : item.name,
    price: item.totals.price,
    memberPrice: item.totals.memberPrice,
    itemType: 'insurance',
    additionalInfo,
    additionalElements: [],
    taxesAndFees: item.totals.taxesAndFees,
    mobileAppPrice: item.mobileAppPrice,
  }
}

interface GetInsuredFlightListItemsParams {
  containsFlighsWithEmbeddedInsurance: boolean
  flights: Array<App.Checkout.FlightItem>
  isStandaloneFlightPurchase: boolean
  checkoutRoute: string | undefined
  hasFullBundleCoverage?: boolean
}

interface getCFMRAdditionalInfoTextParams extends GetInsuredFlightListItemsParams {
  verticalsInCheckout: QuoteVerticals
  hasFullBundleCoverage: boolean | undefined
  containsBedbankWithCFMRFlexPackage: boolean
}

function getInsuredFlightListItems({
  containsFlighsWithEmbeddedInsurance,
  flights,
  isStandaloneFlightPurchase,
  checkoutRoute,
  hasFullBundleCoverage,
}: GetInsuredFlightListItemsParams): Array<string> {
  if (!containsFlighsWithEmbeddedInsurance || !flights.length) return []
  const flight = flights[0]

  const flightsWithInsurance = flight.flights.reduce((acc, flight, index) => {
    if (isFlightWithEmbeddedInsurance(flight)) {
      acc.add(index + 1)
    }
    return acc
  }, new Set<number>())

  const totalFlights = flight.flights.length
  const insuredFlights = [...flightsWithInsurance]

  const intl = getCurrentIntl()
  if (hasFullBundleCoverage ||
    (insuredFlights.length === totalFlights && (isStandaloneFlightPurchase || isCheckoutReviewAndPaymentStepId(checkoutRoute) || totalFlights > 1))
  ) {
    return [intl.formatMessage({
      id: 'vapOuYelnVGsaq-y64l0',
      defaultMessage: 'all your flights',
      description: 'Text indicating all flights are covered by insurance',
    })]
  }

  return insuredFlights.map(n => intl.formatMessage(
    {
      id: '3wYJSpBWILl-WVoXzIUD',
      defaultMessage: 'flight {flightNumber}',
      description: 'Individual flight number label for insurance coverage',
    },
    { flightNumber: n },
  ))
}

function getCFMRAdditionalInfoText({
  containsFlighsWithEmbeddedInsurance,
  containsBedbankWithCFMRFlexPackage,
  flights,
  isStandaloneFlightPurchase,
  hasFullBundleCoverage,
  checkoutRoute,
  verticalsInCheckout,
}: getCFMRAdditionalInfoTextParams) {
  if (!containsFlighsWithEmbeddedInsurance && !containsBedbankWithCFMRFlexPackage) return []

  const intl = getCurrentIntl()
  const flightItems = getInsuredFlightListItems({ containsFlighsWithEmbeddedInsurance, flights, isStandaloneFlightPurchase, checkoutRoute, hasFullBundleCoverage })

  const verticalItems = verticalsInCheckout
    // Exclude 'flight' verticals here since their info is already included in flightItems above
    .filter(vertical => vertical !== 'flights')

  const listItems = [...flightItems, ...verticalItems].filter(item => {
    return hasFullBundleCoverage ||
      (/^(hotel|hotels)$/i.test(item) && containsBedbankWithCFMRFlexPackage) ||
      (/flight/i.test(item) && containsFlighsWithEmbeddedInsurance)
  })
  if (listItems.length === 0) return []

  const combinedListText = formatInlineList(listItems, 'conjunction').toLocaleLowerCase()

  return [
    intl.formatMessage(
      {
        id: 'wrdRzPkxifweiDJV73nj',
        defaultMessage: 'Cancellation Protection included with {combinedList} – 100% refund for unforeseen events',
        description: 'Cancellation protection info text for CFMR insurance in checkout',
      },
      { combinedList: combinedListText },
    ),
  ]
}

const getInsuranceBreakdownView = createSelector(
  (state: App.State) => getInsuranceItemsView(state),
  (state: App.State) => state.geo.insuranceProductName,
  (state: App.State) => checkoutContainsFlightsWithEmbeddedInsurance(state),
  (state: App.State) => checkoutContainsBedbankWithCFMRFlexPackage(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => isCheckoutPostPurchaseInsurance(state),
  (state: App.State) => isStandaloneFlight(state),
  (state: App.State) => getCheckoutRouteFromURL(state.router.location.pathname),
  (state: App.State) => getLuxVerticals(state.checkout.cart.items),
  (state: App.State) => dateFormattingLocaleFromGeo(state.geo),
  (viewWithStatus, insuranceName, containsFlighsWithEmbeddedInsurance, containsBedbankWithCFMRFlexPackage, flights, isPostPurchase, isStandaloneFlightPurchase, checkoutRoute, verticalsInCheckout, dateFormattingLocale): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) {
      return {
        hasRequiredData: viewWithStatus.hasRequiredData,
        data: [],
      }
    }

    const breakdownViews = viewWithStatus.data.map((view): App.Checkout.PriceBreakdownView => {
      const breakdownItem = getInsuranceBreakdownItem(view, isPostPurchase, dateFormattingLocale)
      if (view.item.insuranceType === 'cfmr') {
        return {
          title: containsFlighsWithEmbeddedInsurance && !view.item.hasFullBundleCoverage
            ? knownPriceBreakdownTitle('luxuryEscapesProtect')
            : knownPriceBreakdownTitle('cancellationInsurance', { insuranceProductName: capitalise(insuranceName) }),
          price: view.totals.price + (view.totals.mobileAppDiscount?.amount ?? 0),
          memberPrice: view.totals.memberPrice,
          additionalInfoText: getCFMRAdditionalInfoText({
            containsFlighsWithEmbeddedInsurance,
            containsBedbankWithCFMRFlexPackage,
            flights,
            isStandaloneFlightPurchase,
            hasFullBundleCoverage: view.item.hasFullBundleCoverage,
            checkoutRoute,
            verticalsInCheckout,
          }),
          items: [breakdownItem],
          breakdownItemType: 'bookingProtection',
          alwaysShowItemPrice: !!view.totals.price,
          mobileAppDiscount: view.totals.mobileAppDiscount,
        }
      } else {
        const isPassportCardInsurance =
          view.item.insuranceType === INSURANCE_TYPE_PASSPORT_CARD ||
          view.item.productId === PRODUCT_ID_PASSPORT_CARD

        return {
          title: isPassportCardInsurance
            ? knownPriceBreakdownTitle('travelInsuranceFixed')
            : knownPriceBreakdownTitle('travelInsurance', { insuranceProductName: capitalise(insuranceName) }),
          price: view.totals.price + (view.totals.mobileAppDiscount?.amount ?? 0),
          memberPrice: view.totals.memberPrice,
          additionalInfoText: [],
          items: [breakdownItem],
          breakdownItemType: 'insurance',
          mobileAppDiscount: view.totals.mobileAppDiscount,
        }
      }
    })
    return {
      hasRequiredData: viewWithStatus.hasRequiredData,
      data: breakdownViews,
    }
  },
)

export default getInsuranceBreakdownView
